/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Button, INodeProps, UncontrolledTooltip } from '@msdyn365-commerce-modules/utilities';
import { CartIconComponent } from '@msdyn365-commerce/components';
import MsDyn365, { getUrlSync, IImageSettings, RichText, RichTextComponent } from '@msdyn365-commerce/core';
import { CartLine, CommerceProperty, OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { IFarmlandsCartIconData } from './farmlands-cart-icon.data';
import { IFarmlandsCartIconProps, IFarmlandsCartIconResources } from './farmlands-cart-icon.props.autogenerated';
import { FlyoutCartLineItems, IFlyoutCartLineItemViewProps } from './components/flyout-cart-line-items';
import { getMiniCartData, IMiniCartData } from './utils/get-products-in-mini-cart';
import { hasCartLineValidationErrors, retrieveDeliveryOptionsForCartItems } from '../../helpers/cart-validation-helpers';
import {
    isShareholder,
    isShareholderAccountNotInactive,
    isShareholderAccountNotOnHold,
    hasEcommerceAccess
} from '../../common/method/functions';
import { IDeliveryOptions } from '../../actions/farmlands-get-delivery-options-calls.action';
import { getContactDetailsAsync } from '../../actions/farmlands-get-contact-details-calls.action';
import { inventoryStocks } from '../farmlands-cart/inventoryStore';
import { getEstimatedProductWarehouseAvailabilityAsync } from '../../actions/farmlands-get-estimated-product-warehouse-availability.action';
export interface IMiniCartState {
    miniCartData: IMiniCartData;
    deliveryOptions: IDeliveryOptions[];
    orgUnitLocations?: OrgUnitLocation[];
    isShareholderUser: boolean;
    isAccountNotInactive: boolean;
    isAccountNotOnHold: boolean;
    hasValidationErrors: boolean;
    hasEcomPermission: boolean;
}

export interface ICartIconViewProps extends IFarmlandsCartIconProps<IFarmlandsCartIconData> {
    className: string;
    cartIcon: React.ReactNode;

    FlyoutContainer?: INodeProps;

    CartlinesWrapper: INodeProps;
    miniCartIcon: INodeProps;
    miniCartItemWrapper: INodeProps;
    cartlines?: IFlyoutCartLineItemViewProps[];
    flyoutTitle: React.ReactNode;
    totalPrice: React.ReactNode;

    checkoutButtons?: React.ReactNode;
    goToCartButton?: React.ReactNode;
    waitingComponent?: React.ReactNode;

    isCartLoading: boolean;

    removeItemClickHandler(cartlineToRemove: CartLine): void;
}

/**
 *
 * Cart component
 * @extends {React.PureComponent<ICartProps<ICartData>>}
 */
@observer
class CartIcon extends React.Component<IFarmlandsCartIconProps<IFarmlandsCartIconData>, IMiniCartState> {
    constructor(props: IFarmlandsCartIconProps<IFarmlandsCartIconData>) {
        super(props);
        this.state = {
            miniCartData: {},
            deliveryOptions: [],
            isShareholderUser: false,
            isAccountNotInactive: false,
            isAccountNotOnHold: false,
            hasValidationErrors: false,
            hasEcomPermission: true
        };
    }

    public componentDidMount(): void {
        const context = this.props.context.actionContext;
        const {
            data: {
                cart: { result: cart }
            }
        } = this.props;

        const populateMiniCartData = async () => {
            const cartlines = cart?.cart.CartLines ?? [];

            if (cartlines?.length){
                const firstCartline = cartlines[0];
                const isClickCollect: boolean = firstCartline.FulfillmentStoreId ? true : false;
                const cartLink = getUrlSync('cart', this.props.context.actionContext) || '';
                const isCartPage: boolean = cartLink === this.props.context.request.url.requestUrl.pathname;

                // cart module on cart page calls the API so we do not need to fetch the data again
                if (!isCartPage && isClickCollect){
                    await getEstimatedProductWarehouseAvailabilityAsync({
                        context: { callerContext: this.props.context.actionContext },
                        cartID: cart?.cart.Id
                    })
                    .then((response) => {
                        inventoryStocks.lineItems = response.InventoryStockAvailabilities;
                        inventoryStocks.isClickCollect = isClickCollect;
                    })
                }
            }

            this.setState({
                miniCartData: await getMiniCartData(context)
            });

            await retrieveDeliveryOptionsForCartItems(context, this.state.miniCartData.products!).then((response) => {
                this.setState({
                    deliveryOptions: response
                });
            });
            this.setState({
                hasValidationErrors:
                    cart?.cart.CartLines && this.state.deliveryOptions.length > 0
                        ? hasCartLineValidationErrors(cart.cart.CartLines, this.state.deliveryOptions)
                        : false
            });
        };

        // Initial call for data.
        populateMiniCartData().catch();

        // Reaction for subsequent changes for cartlines.
        reaction(() => this.props.data.cart.result?.cart.CartLines, populateMiniCartData);

        this.setState({
            orgUnitLocations: this.props.data.orgUnitLocations.result
        });

        when(
            () => cart!.status === 'READY',
            async () => {
                // check if the logged-in user has ecomm purchase rights
                if (this.props.context.request.user.token) {
                    const userContactDetails = await getContactDetailsAsync(
                        { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                        JSON.parse(atob(this.props.context.request.user.token.split('.')[1])).oid
                    );
                    if (!hasEcommerceAccess(userContactDetails)) {
                        this.setState({
                            hasEcomPermission: false
                        });
                    }
                }

                this.setState({
                    isShareholderUser: isShareholder(cart?.cart!) || false,
                    isAccountNotInactive: isShareholderAccountNotInactive(cart?.cart.ExtensionProperties!),
                    isAccountNotOnHold: isShareholderAccountNotOnHold(cart?.cart.ExtensionProperties!)
                });

            }
        );
    }

    public render(): JSX.Element | null {
        const {
            id,
            typeName,
            data: {
                cart: { result: cart }
            },
            config: {
                enableHoverCart,
                onHoldMessage,
                suspendedMessage,
                ecomPermissionMessage
            },
            context: {
                request: {
                    user: { isAuthenticated, signInUrl }
                }
            },
            context,
            resources
        } = this.props;

        const cartUrl = getUrlSync('cart', this.props.context.actionContext) || '';
        const cartLines = this._getCartLines();
        const hasErrors = cartLines ? cartLines.some((cartline) => cartline.hasError) : false;
        const backorderError = cart ? this._checkBackorderErrors(cart.cart.CartLines) : false;
        const includeFlyout = this.props.data.cart.status === 'SUCCESS' && enableHoverCart;
        const goToCartButtonText = resources.goToCartButtonTitle.replace('{count}', cart?.totalItemsInCart.toString() || '0');
        const flyoutTitle = resources.flyoutTitle;
        const price =
            cart?.cart.ExtensionProperties?.find((prop: CommerceProperty) => prop.Key === 'TotalSalesLinesIncGst')?.Value?.DecimalValue ||
            undefined;
        const priceString = price
            ? resources.totalPriceFormatString.replace('{price}', context.cultureFormatter.formatCurrency(price))
            : '';
        const disableCheckout = backorderError || hasErrors || this.state.hasValidationErrors || cart?.totalItemsInCart! === 0;
        const inventoryAvaliabilityLineItems = inventoryStocks.lineItems.slice();

        // hotfix: render issue when user hover over cart immediately when inventoryStocks is not ready.
        if (
            inventoryStocks.isClickCollect &&
            (cartLines && cartLines.length !== 0 && inventoryAvaliabilityLineItems?.length !== 0) &&
            !(cartLines?.length >= inventoryAvaliabilityLineItems?.length)
        ){
            return null;
        }

        const viewProps = {
            ...this.props,
            cartIcon: cart && (
                <CartIconComponent
                    cartLabel={resources.cartLabel}
                    cartQtyLabel={resources.cartQtyLabel}
                    context={context}
                    id={id}
                    typeName={typeName}
                    data={{ cart: cart }}
                />
            ),
            cartlines: cartLines,
            FlyoutContainer: includeFlyout
                ? {
                    tag: UncontrolledTooltip,
                    placement: 'bottom-end',
                    hideArrow: true,
                    className: 'ms-cart-icon__flyout-container',
                    trigger: 'hover focus',
                    displayMode: 'FLYOUT',
                    target: id
                }
                : undefined,
            CartlinesWrapper: {
                className: 'ms-cart-icon__cart-lines'
            },
            miniCartIcon: {
                className: classnames('ms-cart-icon')
            },
            miniCartItemWrapper: {
                className: classnames('msc-cart-lines-item')
            },
            checkoutButtons: this._getCheckoutButtons(
                isAuthenticated,
                this.state.isShareholderUser,
                disableCheckout,
                signInUrl!,
                resources,
                onHoldMessage || '',
                suspendedMessage || '',
                ecomPermissionMessage || ''
            ),
            goToCartButton: (
                <Button className={'ms-cart-icon__btn-gotocart'} href={cartUrl} title={goToCartButtonText}>
                    {goToCartButtonText}
                </Button>
            ),
            flyoutTitle: <span className={`ms-cart-icon__flyout-title`}>{flyoutTitle}</span>,
            totalPrice: <div className={`ms-cart-icon__subtotal`}>{priceString}</div>
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _getCheckoutButtons(
        isAuthenticated: boolean,
        isShareholderUser: boolean,
        disableCheckoutButton: boolean,
        signInUrl: string,
        resources: IFarmlandsCartIconResources,
        onHoldMessage: RichText,
        suspendedMessage: RichText,
        ecomPermissionMessage: RichText
    ): React.ReactNode {
        const checkoutURL = getUrlSync('checkout', this.props.context.actionContext) || '';
        const checkoutAsShareholderURL = getUrlSync('checkout-shareholder-only', this.props.context.actionContext) || '';
        const signInURL = this._getSignInURL(signInUrl || '');
        const { isAccountNotInactive, isAccountNotOnHold, hasEcomPermission } = this.state;

        return (
            <>
                {isAuthenticated && isShareholderUser && (
                    <>
                        <Button
                            disabled={disableCheckoutButton || !(isAccountNotOnHold && isAccountNotInactive) || !hasEcomPermission}
                            className={'ms-cart-icon__btn-checkout'}
                            href={checkoutAsShareholderURL}
                            title={resources.checkoutWithAccountButtonTitle}
                        >
                            {resources.checkoutWithAccountButtonTitle}
                        </Button>
                        {!isAccountNotOnHold && <RichTextComponent className='shareholder-warning' text={onHoldMessage} />}
                        {!isAccountNotInactive && <RichTextComponent className='shareholder-warning' text={suspendedMessage} />}
                        {!hasEcomPermission && <RichTextComponent className='shareholder-warning' text={ecomPermissionMessage} />}
                    </>
                )}
                {isAuthenticated && (
                    <Button
                        disabled={disableCheckoutButton || !hasEcomPermission}
                        className={'ms-cart-icon__btn-checkout'}
                        href={checkoutURL}
                        title={resources.checkoutWithCardButtonTitle}
                    >
                        {resources.checkoutWithCardButtonTitle}
                    </Button>
                )}
                {!isAuthenticated && (
                    <Button
                        disabled={disableCheckoutButton || !hasEcomPermission}
                        className={'ms-cart-icon__btn-checkout'}
                        href={signInURL}
                        title={resources.checkoutButtonTitle}
                    >
                        {resources.checkoutButtonTitle}
                    </Button>
                )}
                {!isAuthenticated && (
                    <Button
                        disabled={disableCheckoutButton || !hasEcomPermission}
                        className={'ms-cart-icon__btn-checkout'}
                        href={checkoutURL}
                        title={resources.guestCheckoutButtonTitle}
                    >
                        {resources.guestCheckoutButtonTitle}
                    </Button>
                )}
            </>
        );
    }

    private _getSignInURL(signInUrl: string): string {
        if (MsDyn365.isBrowser) {
            return `${signInUrl}${signInUrl.indexOf('?') === -1 ? '?' : '&'}ru=${window.location.origin}/cart`;
        }
        return '';
    }

    private _getCartLines(): IFlyoutCartLineItemViewProps[] | null {
        const {
            data: {
                cart: { result: cart }
            },
            context: {
                request: { channel: { PickupDeliveryModeCode } = { PickupDeliveryModeCode: undefined } }
            },
            config: config,
            telemetry,
            resources
        } = this.props;

        const imageSettings = this._getImageSettings(config.imageSettings);

        return this.state.miniCartData.products && cart && cart.cart.CartLines && cart.cart.CartLines.length > 0
            ? FlyoutCartLineItems({
                cartlines: cart.cart.CartLines,
                products: this.state.miniCartData.products,
                deliveryOptions: this.state.deliveryOptions,
                productAvailabilites: this.state.miniCartData.productAvailabilites,
                productDeliveryOptions: this.state.miniCartData.deliveryOptions,
                imageSettings: imageSettings,
                outOfStockThreshold: this.props.context.app.config.outOfStockThreshold || 0,
                isStockCheckEnabled: this.props.context.app.config.enableStockCheck || false,
                maxCartlineQuantity: this.props.context.app.config.maxQuantityForCartLineItem,
                gridSettings: this.props.context.request.gridSettings!,
                context: this.props.context,
                typeName: this.props.typeName,
                id: this.props.id,
                resources: {
                    sizeString: resources.productDimensionTypeSize,
                    colorString: resources.productDimensionTypeColor,
                    configString: resources.productDimensionTypeConfiguration,
                    styleString: resources.productDimensionTypeStyle,
                    quantityDisplayString: resources.quantityDisplayText,
                    inputQuantityAriaLabel: '', // not used
                    discountStringText: '', // not used
                    originalPriceText: '',
                    currentPriceText: '',
                    shippingChargesText: '',
                    inventoryAvaliabilityMessages: {
                        clickCollectText: config.clickCollectText,
                        deliveryText: config.deliveryText,
                        outOfStockText: config.outOfStockText,
                        noBackOrder: config.noBackOrder,
                        clickCollectOutOfStock: config.clickCollectOutOfStock,
                        deliveryOutOfStock: config.deliveryOutOfStock
                    }
                },
                telemetry: telemetry,
                removeButtonText: resources.removeCartButtonText,
                outOfStockText: '', // not used
                outOfRangeOneText: '', // not used
                outOfRangeFormatText: '', // not used
                validationErrorRemoveItemText: resources.validationErrorRemoveItemText,
                cartState: this.props.data.cart.result,
                orgUnitLocations: this.state.orgUnitLocations,
                removeItemClickHandler: this._removeItemFromCartHandler,
                pickupDeliveryModeCode: PickupDeliveryModeCode
            })
            : null;
    }

    private _getImageSettings = (imageSettings: IImageSettings | undefined): IImageSettings => {
        return (
            imageSettings || {
                viewports: {
                    xs: { q: `w=80&h=94&m=6`, w: 0, h: 0 },
                    sm: { q: `w=148&h=174&m=6`, w: 0, h: 0 },
                    lg: { q: `w=148&h=174&m=6`, w: 0, h: 0 }
                },
                lazyload: true
            }
        );
    };

    private _removeItemFromCartHandler = (cartlineToRemove: CartLine) => {
        if (this.props.data.cart.result) {
            const input = {
                cartLineIds: [cartlineToRemove.LineId!.toString()]
            };
            this.props.data.cart.result.removeCartLines(input).catch((error) => {
                this.props.telemetry.warning(error);
                this.props.telemetry.debug('Unable to Remove Cart Line');
            });
        }
    };

    private _checkBackorderErrors = (cart: any) => {
        const { lineItems } = inventoryStocks;
        const currentInventoryStocks = lineItems.filter(p1 => cart.some((p2: any) => p1['ProductId'] === p2['ProductId']))
        return !!currentInventoryStocks.some((item) => item['PhysicalAvailableInventoryLevelCode'] === 'OOS' && !item['IsBackOrderAllowed']);
    };
}

export default CartIcon;
